// Performance monitoring utilities
// Track Core Web Vitals and custom metrics

import { getOpenPanel } from './openpanel';

// Track performance metrics
export const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    });
  }
};

// Send metrics to analytics
export const sendToAnalytics = (metric) => {
  // Send to OpenPanel if available
  getOpenPanel()?.track('web_vital', {
    name: metric.name,
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    id: metric.id,
  });

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('Performance Metric:', {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
    });
  }
};

// Monitor component render time
export const measureComponentRender = (componentName, callback) => {
  const start = performance.now();
  
  callback();
  
  const end = performance.now();
  const duration = end - start;
  
  if (duration > 16) { // Warn if render takes more than one frame (16ms)
    console.warn(`${componentName} rendered in ${duration.toFixed(2)}ms`);
  }
  
  return duration;
};

// Track navigation timing
export const getNavigationTiming = () => {
  if (!window.performance || !window.performance.timing) {
    return null;
  }

  const timing = window.performance.timing;
  const navigation = {
    // Page load times
    dnsLookup: timing.domainLookupEnd - timing.domainLookupStart,
    tcpConnection: timing.connectEnd - timing.connectStart,
    serverResponse: timing.responseEnd - timing.requestStart,
    domParsing: timing.domInteractive - timing.domLoading,
    domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
    pageLoad: timing.loadEventEnd - timing.navigationStart,
    
    // First paint
    firstPaint: performance.getEntriesByType('paint')[0]?.startTime || 0,
    firstContentfulPaint: performance.getEntriesByType('paint')[1]?.startTime || 0,
  };

  return navigation;
};

// Track resource loading
export const getResourceTiming = () => {
  if (!window.performance || !window.performance.getEntriesByType) {
    return [];
  }

  return performance.getEntriesByType('resource').map(resource => ({
    name: resource.name,
    duration: resource.duration,
    size: resource.transferSize,
    type: resource.initiatorType,
  }));
};

// Monitor memory usage (if available)
export const getMemoryUsage = () => {
  if (window.performance && window.performance.memory) {
    return {
      usedJSHeapSize: window.performance.memory.usedJSHeapSize,
      totalJSHeapSize: window.performance.memory.totalJSHeapSize,
      jsHeapSizeLimit: window.performance.memory.jsHeapSizeLimit,
      usagePercentage: (window.performance.memory.usedJSHeapSize / window.performance.memory.jsHeapSizeLimit * 100).toFixed(2),
    };
  }
  return null;
};

// Track long tasks (requires PerformanceObserver)
export const observeLongTasks = (callback) => {
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) { // Tasks longer than 50ms
            callback({
              name: entry.name,
              duration: entry.duration,
              startTime: entry.startTime,
            });
          }
        }
      });
      
      observer.observe({ entryTypes: ['longtask'] });
      return observer;
    } catch (e) {
      console.warn('Long task observation not supported');
    }
  }
  return null;
};

// Performance budget checker
export const checkPerformanceBudget = () => {
  const budgets = {
    FCP: 1800,  // First Contentful Paint (ms)
    LCP: 2500,  // Largest Contentful Paint (ms)
    FID: 100,   // First Input Delay (ms)
    CLS: 0.1,   // Cumulative Layout Shift
    TTFB: 600,  // Time to First Byte (ms)
  };

  const results = {};
  
  // Check if metrics are within budget
  if (window.performance && window.performance.timing) {
    const timing = window.performance.timing;
    const ttfb = timing.responseStart - timing.requestStart;
    results.TTFB = {
      value: ttfb,
      budget: budgets.TTFB,
      withinBudget: ttfb <= budgets.TTFB,
    };
  }

  return results;
};

// Utility to format bytes
export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// Generate performance report
export const generatePerformanceReport = () => {
  const report = {
    timestamp: new Date().toISOString(),
    navigation: getNavigationTiming(),
    resources: getResourceTiming(),
    memory: getMemoryUsage(),
    budget: checkPerformanceBudget(),
  };

  if (process.env.NODE_ENV === 'development') {
    console.group('📊 Performance Report');
    console.table(report.navigation);
    console.log('Memory Usage:', report.memory);
    console.log('Budget Check:', report.budget);
    console.groupEnd();
  }

  return report;
};

const performanceUtils = {
  reportWebVitals,
  sendToAnalytics,
  measureComponentRender,
  getNavigationTiming,
  getResourceTiming,
  getMemoryUsage,
  observeLongTasks,
  checkPerformanceBudget,
  generatePerformanceReport,
  formatBytes,
};

export default performanceUtils;
